import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useAuth } from 'react-oidc-context';
import { api } from '../api/client';
import type { MyApplication, MyProfile } from '../api/types';
import { PhotoPicker, type PhotoChange } from '../components/PhotoPicker';
import { RolePill } from '../components/RolePill';
import { hasClientRole } from '../lib/roles';
import { getRuntimeConfig } from '../runtimeConfig';
import { useSession } from '../state/SessionContext';
import { useToast } from '../state/ToastContext';
import { appHref, expiryLabel } from '../utils/applications';
import { shortDate } from '../utils/format';
import { PASSWORD_MIN_LENGTH, passwordProblem } from '../utils/password';
import { photoSrc } from '../utils/photo';
import {
  detailsChanged,
  detailsRequest,
  displayName,
  profileDetails,
  type ProfileDetailKey,
  type ProfileDetails,
} from '../utils/profile';

interface DetailField {
  key: ProfileDetailKey;
  label: string;
  type: 'text' | 'email' | 'tel';
  autoComplete: string;
  wide?: boolean;
}

const DETAIL_FIELDS: DetailField[] = [
  { key: 'firstName', label: 'First name', type: 'text', autoComplete: 'given-name' },
  { key: 'lastName', label: 'Last name', type: 'text', autoComplete: 'family-name' },
  { key: 'phone', label: 'Telephone', type: 'tel', autoComplete: 'work tel' },
  { key: 'personalPhone', label: 'Personal phone', type: 'tel', autoComplete: 'home tel' },
  {
    key: 'personalEmail',
    label: 'Personal email',
    type: 'email',
    autoComplete: 'home email',
    wide: true,
  },
  { key: 'address', label: 'Address', type: 'text', autoComplete: 'street-address', wide: true },
];

function errorText(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function ProfilePage() {
  const auth = useAuth();
  const { me, reloadMe } = useSession();
  const { showToast } = useToast();
  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [version, setVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [photo, setPhoto] = useState<PhotoChange>({ kind: 'unchanged' });
  const [photoBusy, setPhotoBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setProfile(await api.myProfile());
      setVersion((current) => current + 1);
      setError(null);
    } catch (cause) {
      setError(errorText(cause));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function detailsSaved() {
    showToast('Profile updated');
    await auth.signinSilent();
    await reloadMe().catch(() => undefined);
    await load();
  }

  async function photoSaved(label: string) {
    showToast(label);
    await auth.signinSilent().catch(() => undefined);
    await reloadMe().catch(() => undefined);
    await load();
  }

  async function changePhoto(next: PhotoChange) {
    setPhoto(next);
    if (next.kind === 'unchanged') {
      return;
    }
    setPhotoBusy(true);
    setError(null);
    try {
      if (next.kind === 'replace') {
        await api.uploadMyPhoto(next.blob);
      } else {
        await api.deleteMyPhoto();
      }
      await photoSaved(next.kind === 'replace' ? 'Photo updated' : 'Photo removed');
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setPhoto({ kind: 'unchanged' });
      setPhotoBusy(false);
    }
  }

  if (!profile) {
    return (
      <div className="page page-narrow">
        <h1>My profile</h1>
        {error ? (
          <div className="error-banner">Could not load your profile: {error}</div>
        ) : (
          <div className="empty-state">Loading profile…</div>
        )}
      </div>
    );
  }

  const name = displayName(profile);

  return (
    <div className="page page-narrow">
      <div className="detail-header">
        <PhotoPicker
          variant="header"
          name={name}
          currentSrc={photoSrc(getRuntimeConfig().apiUrl, profile.id, profile.photoVersion)}
          value={photo}
          onChange={(next) => void changePhoto(next)}
          disabled={photoBusy}
        />
        <div className="detail-header-meta">
          <div className="detail-title-row">
            <h1>{name}</h1>
            <span className="kc-status-caption">My profile</span>
          </div>
          <div className="detail-subtitle">
            <span className="mono">{profile.username}</span>
            {profile.email && <> · {profile.email}</>}
          </div>
        </div>
      </div>

      {!hasClientRole(me.clientRole) && (
        <div className="muted-note profile-note">
          Your account has no UDB Console role — only your own profile is available here.
        </div>
      )}
      {error && <div className="error-banner">{error}</div>}

      <div className="detail-grid">
        <div className="detail-side">
          <WorkDetailsPanel profile={profile} />
          <ApplicationsPanel />
        </div>
        <div className="profile-main">
          <PersonalDetailsPanel key={version} profile={profile} onSaved={detailsSaved} />
          <PasswordPanel username={profile.username} otpEnabled={profile.otpEnabled} />
        </div>
      </div>
    </div>
  );
}

function ApplicationsPanel() {
  const [apps, setApps] = useState<MyApplication[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .myApplications()
      .then((result) => {
        if (active) {
          setApps(result);
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(errorText(cause));
        }
      });
    return () => {
      active = false;
    };
  }, []);

  let body = <div className="muted-note">Loading applications…</div>;
  if (error) {
    body = <div className="muted-note">Could not load your applications: {error}</div>;
  } else if (apps && apps.length === 0) {
    body = (
      <div className="muted-note">
        You have no application access yet — ask the back office for what you need.
      </div>
    );
  } else if (apps) {
    body = (
      <div className="apps-list">
        {apps.map((app) => (
          <ApplicationRow key={app.id} app={app} />
        ))}
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-label">Your applications</div>
      {body}
      {apps && apps.length > 0 && (
        <div className="muted-note panel-footnote">
          Each opens in a new tab — sign in with this same account.
        </div>
      )}
    </div>
  );
}

function ApplicationRow({ app }: { app: MyApplication }) {
  const href = appHref(app.url);
  const expiry = expiryLabel(app.expiresAt);
  const inner = (
    <>
      <div className="sys-icon" style={{ background: app.color }}>
        {app.abbr}
      </div>
      <div className="app-body">
        <div className="app-name">{app.name}</div>
        <div className="app-cat">{app.category}</div>
      </div>
      <div className="app-foot">
        <div className="app-meta">
          <div className="app-roles">
            {app.roles.map((role) => (
              <RolePill key={role} role={role} />
            ))}
          </div>
          {expiry && <div className="app-expiry">{expiry}</div>}
        </div>
        <span className={href ? 'app-open' : 'app-nolink'}>{href ? 'Open ↗' : 'No link'}</span>
      </div>
    </>
  );

  return href ? (
    <a className="app-link" href={href} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  ) : (
    <div className="app-link app-link-static">{inner}</div>
  );
}

function WorkDetailsPanel({ profile }: { profile: MyProfile }) {
  const rows: Array<{ label: string; value: string | null; mono?: boolean }> = [
    { label: 'Work email', value: profile.email },
    { label: 'Position', value: profile.title },
    { label: 'Department', value: profile.department },
    { label: 'Manager', value: profile.manager },
    { label: 'Location', value: profile.location },
    { label: 'Company start', value: profile.companyStart, mono: true },
    { label: 'Company leave', value: profile.companyLeave, mono: true },
    { label: 'Account created', value: shortDate(profile.joinedAt), mono: true },
  ];

  return (
    <div className="panel">
      <div className="panel-label">Work details</div>
      <div className="field-list">
        {rows.map((row) => (
          <div key={row.label}>
            <div className="field-label">{row.label}</div>
            {row.mono ? <span className="mono">{row.value ?? '—'}</span> : (row.value ?? '—')}
          </div>
        ))}
        <div>
          <div className="field-label">Authenticator app</div>
          <span className={profile.otpEnabled ? 'mfa-on' : 'mfa-off'}>
            {profile.otpEnabled ? 'Enabled' : 'Not set'}
          </span>
        </div>
      </div>
      <div className="muted-note panel-footnote">
        Managed by your administrator — ask the back office to change these details.
      </div>
    </div>
  );
}

function PersonalDetailsPanel({
  profile,
  onSaved,
}: {
  profile: MyProfile;
  onSaved: () => Promise<void>;
}) {
  const [saved] = useState<ProfileDetails>(() => profileDetails(profile));
  const [values, setValues] = useState<ProfileDetails>(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = detailsChanged(saved, values);
  const set = (key: ProfileDetailKey, value: string) =>
    setValues((previous) => ({ ...previous, [key]: value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!dirty || saving) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.updateMyProfile(detailsRequest(values));
    } catch (cause) {
      setError(errorText(cause));
      setSaving(false);
      return;
    }
    try {
      await onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="panel" onSubmit={(event) => void submit(event)}>
      <div className="panel-label">Personal details</div>
      <div className="form-grid">
        {DETAIL_FIELDS.map((field) => (
          <label
            key={field.key}
            className={`form-field${field.wide ? ' form-field-wide' : ''}`}
          >
            <span className="form-label">{field.label}</span>
            <input
              className="form-input"
              type={field.type}
              autoComplete={field.autoComplete}
              maxLength={255}
              value={values[field.key]}
              onChange={(event) => set(field.key, event.target.value)}
            />
          </label>
        ))}
      </div>
      {error && <div className="modal-error">{error}</div>}
      <div className="panel-actions">
        <button
          type="button"
          className="btn-secondary"
          disabled={!dirty || saving}
          onClick={() => {
            setValues(saved);
            setError(null);
          }}
        >
          Reset
        </button>
        <button type="submit" className="btn-primary" disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

function PasswordPanel({ username, otpEnabled }: { username: string; otpEnabled: boolean }) {
  const { showToast } = useToast();
  const [current, setCurrent] = useState('');
  const [otp, setOtp] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problem =
    passwordProblem(current, next, confirm) ??
    (otpEnabled && otp.trim().length === 0
      ? 'Enter the code from your authenticator app.'
      : null);
  const showProblem = next.length > 0 || confirm.length > 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (problem !== null || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.changeMyPassword({
        currentPassword: current,
        newPassword: next,
        otp: otpEnabled ? otp.trim() : null,
      });
      setCurrent('');
      setNext('');
      setConfirm('');
      showToast('Password changed');
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setOtp('');
      setSubmitting(false);
    }
  }

  return (
    <form className="panel" onSubmit={(event) => void submit(event)}>
      <div className="panel-label">Change password</div>
      <input
        type="text"
        name="username"
        autoComplete="username"
        value={username}
        readOnly
        hidden
      />
      <div className="form-grid">
        <label className="form-field">
          <span className="form-label">
            Current password <span className="form-req">*</span>
          </span>
          <input
            className="form-input"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
        </label>
        {otpEnabled ? (
          <label className="form-field">
            <span className="form-label">
              Authenticator code <span className="form-req">*</span>
            </span>
            <input
              className="form-input mono"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={10}
              value={otp}
              onChange={(event) => setOtp(event.target.value)}
            />
          </label>
        ) : (
          <div />
        )}
        <label className="form-field">
          <span className="form-label">
            New password <span className="form-req">*</span>
          </span>
          <input
            className="form-input"
            type="password"
            autoComplete="new-password"
            placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
        </label>
        <label className="form-field">
          <span className="form-label">
            Confirm new password <span className="form-req">*</span>
          </span>
          <input
            className="form-input"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </label>
      </div>
      <div className="muted-note password-note">
        {showProblem && problem
          ? problem
          : 'The new password is used for every Medical Insider system that signs in through Keycloak.'}
      </div>
      {error && <div className="modal-error">{error}</div>}
      <div className="panel-actions">
        <button type="submit" className="btn-primary" disabled={problem !== null || submitting}>
          {submitting ? 'Changing…' : 'Change password'}
        </button>
      </div>
    </form>
  );
}
